import { useState, useEffect, useRef } from "react";
import { useParams, useLocation, Link } from "react-router-dom";
import { ArrowLeft, MoreHorizontal, Paperclip, Send as SendIcon, FileText, ImageIcon, Clock, CheckCircle, AlertCircle, XCircle, PlayCircle, Italic, Download, X } from "lucide-react";
import EmojiPicker from "emoji-picker-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import { Document, Page, pdfjs } from 'react-pdf';
import CaSidebar from "./CaSidebar";
import CaHeader from "./CaHeader";
import { getCaFormMessages, sendCaFormMessage, getFormFields, uploadFile, deleteFile, updateFormResponseStatus, uploadImage } from "../utils/api";
import { format } from 'date-fns';
import io from "socket.io-client";

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const TICKET_STATUS = {
  open: { label: "Open", color: "bg-blue-100 text-blue-800", icon: AlertCircle },
  in_progress: { label: "In Progress", color: "bg-yellow-100 text-yellow-800", icon: Clock },
  pending: { label: "Pending", color: "bg-orange-100 text-orange-800", icon: PlayCircle },
  completed: { label: "Completed", color: "bg-green-100 text-green-800", icon: CheckCircle },
  closed: { label: "Closed", color: "bg-gray-100 text-gray-800", icon: XCircle },
};

function Avatar({ name }) {
  const initial = name?.charAt(0).toUpperCase() || "?";
  return (
    <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center text-white font-semibold">
      {initial}
    </div>
  );
}

function StatusBadge({ status, onChange }) {
  const cfg = TICKET_STATUS[status] || TICKET_STATUS.open;
  const Icon = cfg.icon;
  const [open, setOpen] = useState(false);

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        className={`inline-flex items-center space-x-1 px-3 py-1 rounded-lg text-sm ${cfg.color}`}
      >
        <Icon className="w-4 h-4" />
        <span>{cfg.label}</span>
        <MoreHorizontal className="w-4 h-4" />
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-40 bg-white border rounded-lg shadow-lg z-50">
          {Object.entries(TICKET_STATUS).map(([key, c]) => (
            <button
              key={key}
              onClick={() => { onChange(key); setOpen(false); }}
              className="w-full text-left px-4 py-2 hover:bg-gray-50 flex items-center space-x-2"
            >
              <c.icon className="w-4 h-4" />
              <span>{c.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function FilePreviewModal({ file, onClose }) {
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const isPdf = file?.url?.endsWith('.pdf');
  const isImage = file?.type === 'image' || file?.url?.match(/\.(jpg|jpeg|png|gif|webp)$/i);

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setPageNumber(1);
  };

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = file.url;
    link.download = file.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex justify-between items-center border-b p-4">
          <h3 className="text-lg font-medium">{file.name}</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="w-6 h-6" />
          </button>
        </div>
        <div className="flex-1 overflow-auto p-4 flex flex-col items-center">
          {isPdf ? (
            <>
              <div className="border rounded-lg overflow-hidden mb-4">
                <Document
                  file={file.url}
                  onLoadSuccess={onDocumentLoadSuccess}
                  loading={<div className="p-8">Loading PDF...</div>}
                  error={<div className="p-8 text-red-500">Failed to load PDF</div>}
                >
                  <Page
                    pageNumber={pageNumber}
                    width={Math.min(800, window.innerWidth - 100)}
                    renderTextLayer={false}
                  />
                </Document>
              </div>
              <div className="flex items-center justify-center space-x-4">
                <button
                  disabled={pageNumber <= 1}
                  onClick={() => setPageNumber(p => Math.max(1, p - 1))}
                  className="px-4 py-2 bg-gray-200 rounded disabled:opacity-50"
                >
                  Previous
                </button>
                <span>
                  Page {pageNumber} of {numPages || '--'}
                </span>
                <button
                  disabled={pageNumber >= (numPages || 0)}
                  onClick={() => setPageNumber(p => Math.min(numPages || p, p + 1))}
                  className="px-4 py-2 bg-gray-200 rounded disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </>
          ) : isImage ? (
            <img
              src={file.url}
              alt={file.name}
              className="max-h-[70vh] max-w-full object-contain"
            />
          ) : (
            <div className="flex flex-col items-center justify-center p-8">
              <FileText className="w-16 h-16 text-gray-400 mb-4" />
              <p className="text-gray-500 mb-6">Preview not available for this file type</p>
            </div>
          )}
        </div>
        <div className="border-t p-4 flex justify-between items-center">
          <div className="text-sm text-gray-500">
            {file.size ? `Size: ${formatFileSize(file.size)}` : ''}
          </div>
          <button
            onClick={handleDownload}
            className="flex items-center space-x-2 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg"
          >
            <Download className="w-4 h-4" />
            <span>Download</span>
          </button>
        </div>
      </div>
    </div>
  );
}

function formatFileSize(bytes) {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

function MessageStatus({ status, isCurrentUser }) {
  if (!isCurrentUser) return null;

  return (
    <span className="ml-2">
      {status === 'read' ? (
        <span className="text-blue-500">✓✓</span>
      ) : status === 'delivered' ? (
        <span className="text-gray-400">✓</span>
      ) : (
        <span className="text-gray-300">✓</span>
      )}
    </span>
  );
}

const formatTime = ts => {
  if (!ts) return '';
  try {
    const date = new Date(ts);
    if (isNaN(date.getTime())) return '';
    return format(date, "hh:mm a");
  } catch {
    return '';
  }
};

const getFileIcon = (type, url) => {
  if (type === 'pdf' || url?.endsWith('.pdf')) return <FileText className="w-4 h-4 text-red-500" />;
  if (type === 'image' || url?.match(/\.(jpg|jpeg|png|gif|webp)$/i)) return <ImageIcon className="w-4 h-4 text-blue-500" />;
  return <FileText className="w-4 h-4 text-gray-500" />;
};

export default function CATicketConversation({ ticket, currentUser, onClose }) {
  const { ticketId } = useParams();
  const location = useLocation();

  const storedUser = localStorage.getItem('caId');
  const caId = localStorage.getItem('caId');
  const caName = localStorage.getItem('caName') || 'CA';
  const caRole = 'CA';

  const scrollRef = useRef(null);
  const socketRef = useRef(null);
  const textareaRef = useRef();
  const fileInputRef = useRef();
  const imageInputRef = useRef();

  const [fields, setFields] = useState([]);
  const [responseData, setResponseData] = useState({});
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [showAttachMenu, setShowAttachMenu] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [status, setStatus] = useState('open');
  const [startupInfo, setStartupInfo] = useState({ fullName: 'Startup' });
  const [formData, setFormData] = useState(null);
  const [isChatVisible, setIsChatVisible] = useState(true);

  const mergedTicket = {
    id: ticket?.id || location.state?.ticket?.id || ticketId,
    title: ticket?.name || location.state?.ticket?.name || '',
    status: ticket?.status || location.state?.ticket?.status || 'open',
    assignedCA: {
      name:
        ticket?.assignedCA?.name ||
        location.state?.ticket?.assignedCA?.name ||
        storedUser.name ||
        'Startup'
    },
    startup: {
      fullName:
        ticket?.startup?.fullName ||
        location.state?.ticket?.startup?.fullName ||
        'Startup'
    }
  };

  const normalizeMessage = (msg) => {
    const isCurrentUser = msg.senderId?.toString() === caId?.toString();
    const isUser = !isCurrentUser && (msg.senderRole === 'USER' ||
      (msg.senderId && msg.senderId !== caId));

    return {
      ...msg,
      id: msg.id || msg._id || `temp-${Date.now()}`,
      senderId: msg.senderId || (isCurrentUser ? caId : msg.senderId),
      senderName: msg.senderName ||
        (isCurrentUser ? caName :
          (isUser ? mergedTicket.startup.fullName : 'CA')),
      senderRole: msg.senderRole || (isCurrentUser ? 'CA' : 'USER'),
      timestamp: msg.timestamp || new Date().toISOString(),
      isCurrentUser
    };
  };

  useEffect(() => {
    if (!ticketId) return;

    (async () => {
      try {
        console.log(`[CA] Fetching data for ticket ${ticketId}`);
        const [fieldsRes, payload] = await Promise.all([
          getFormFields(ticketId),
          getCaFormMessages(ticketId),
        ]);

        console.log('[CA] raw fieldsRes:', fieldsRes);
        console.log('[CA] raw payload:', payload);

        // Normalize/flatten responseData so downstream rendering is consistent
        // Cases handled:
        // 1) payload = { data: { ...answers }, messages: [], time: ... }
        // 2) payload = { data: { data: { ...answers } }, messages: [], time: ... }
        // 3) payload already is the answers object
        let answers = payload?.data ?? payload ?? {};
        if (answers && typeof answers === 'object' && answers.data) {
          answers = answers.data;
        }

        // Keep createdAt/time available in the response object for formatting
        const createdAt = payload?.time ?? payload?.createdAt ?? answers?.createdAt;
        if (createdAt) answers = { ...answers, _createdAt: createdAt };

        setFields(fieldsRes || []);
        setResponseData(answers || {});
        setMessages((payload?.messages || []).map(normalizeMessage));

        // startupName may be in payload.startupName or payload.startup.fullName
        const fullName = payload?.startupName || payload?.startup?.fullName || payload?.startup?.name;
        if (fullName) setStartupInfo({ fullName });

        console.log('[CA] normalized answers:', answers);
      } catch (err) {
        console.error("[CA] Fetch error:", err);
      }
    })();

    // socket setup (unchanged) ...
    const socket = io(process.env.REACT_APP_API_URL || "http://localhost:8000", {
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    });

    socketRef.current = socket;
    console.log(`[CA] Creating socket for ticket ${ticketId}`);

    socket.on("connect", () => {
      console.log("[CA] Socket connected:", socket.id);
      socket.emit("joinForm", ticketId);
      console.log(`[CA] Emitted joinForm for ticket ${ticketId}`);
    });

    const handleNewMessage = (rawMsg) => {
      setMessages(prev => {
        const exists = prev.some(m =>
          m.id === rawMsg.id ||
          (rawMsg.tempId && m.tempId === rawMsg.tempId)
        );
        if (exists) return prev;
        return [...prev, normalizeMessage(rawMsg)];
      });
    };

    socket.on("newMessage", handleNewMessage);
    socket.on("error", (err) => console.error("[CA] Socket error:", err));
    socket.on("disconnect", () => console.log("[CA] Socket disconnected"));
    socket.on("reconnect", (attempt) => console.log(`[CA] Socket reconnected after ${attempt} attempts`));

    return () => {
      console.log("[CA] Cleaning up socket");
      socket.off("newMessage", handleNewMessage);
      socket.disconnect();
    };
  }, [ticketId]);


  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages]);

  useEffect(() => {
    if (!socketRef.current || !mergedTicket.id) return;

    const handleMessageDelivered = ({ messageId, status }) => {
      setMessages(prev => prev.map(msg =>
        msg.id === messageId ? { ...msg, status } : msg
      ));
    };

    const handleMessagesRead = ({ messageIds }) => {
      setMessages(prev => prev.map(msg =>
        messageIds.includes(msg.id) ? { ...msg, status: 'read' } : msg
      ));
    };

    socketRef.current.on('messageStatusUpdate', handleMessageDelivered);
    socketRef.current.on('messagesRead', handleMessagesRead);

    return () => {
      socketRef.current.off('messageStatusUpdate', handleMessageDelivered);
      socketRef.current.off('messagesRead', handleMessagesRead);
    };
  }, [mergedTicket.id]);

  useEffect(() => {
    if (!socketRef.current?.connected || !messages.length) return;

    const undelivered = messages.filter(
      msg => !msg.isCurrentUser && msg.status !== 'delivered' && msg.status !== 'read'
    );

    if (undelivered.length) {
      socketRef.current.emit('markAsDelivered', {
        messageId: undelivered[0].id,
        formId: mergedTicket.id
      });
    }
  }, [messages, mergedTicket.id]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      const isTabVisible = document.visibilityState === 'visible';
      const isWindowFocused = document.hasFocus();
      const isActuallyVisible = isTabVisible && isWindowFocused;

      console.log('Visibility changed:', {
        isTabVisible,
        isWindowFocused,
        isActuallyVisible
      });

      setIsChatVisible(isActuallyVisible);

      if (isActuallyVisible) {
        setTimeout(() => {
          const container = scrollRef.current;
          if (container) {
            container.dispatchEvent(new Event('scroll'));
          }
        }, 500);
      }
    };

    window.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleVisibilityChange);
    window.addEventListener('blur', handleVisibilityChange);

    handleVisibilityChange();

    return () => {
      window.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleVisibilityChange);
      window.removeEventListener('blur', handleVisibilityChange);
    };
  }, []);

  const handleStatusChange = async (newStatus) => {
    setStatus(newStatus);
    try {
      await updateFormResponseStatus(mergedTicket.id, newStatus);
    } catch (err) {
      console.error('Failed to update status:', err);
    }
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    const uploaded = [];
    for (let file of files) {
      try {
        let res;
        if (file.type.startsWith('image/')) {
          res = await uploadImage(file);
        } else {
          res = await uploadFile(file);
        }

        uploaded.push({
          name: file.name,
          size: res.bytes,
          type: file.type.startsWith('image/') ? 'image' :
            file.name.endsWith('.pdf') ? 'pdf' : 'doc',
          url: res.url,
          publicId: res.publicId,
        });
      } catch (err) {
        console.error('Upload failed:', err);
      }
    }
    setAttachments(prev => [...prev, ...uploaded]);
    setShowAttachMenu(false);
  };

  const handleFilePreview = (file) => {
    setPreviewFile(file);
  };

  const handleDownloadFile = (url, name) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleEmojiClick = (emojiData) => {
    setInput(prev => prev + emojiData.emoji);
    setShowEmojiPicker(false);
  };

  const wrapSelection = (prefix, suffix = '') => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = input.substring(start, end);
    const before = input.substring(0, start);
    const after = input.substring(end);

    setInput(before + prefix + selectedText + suffix + after);

    setTimeout(() => {
      textarea.selectionStart = start + prefix.length;
      textarea.selectionEnd = start + prefix.length + selectedText.length;
      textarea.focus();
    }, 0);
  };

  const insertLink = () => {
    const url = prompt("URL:");
    if (!url) return;
    wrapSelection("[", "](" + url + ")");
  };

  const handleKeyPress = e => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const removeAttachment = async (idx) => {
    const att = attachments[idx];
    try {
      await deleteFile(att.publicId);
      setAttachments(prev => prev.filter((_, i) => i !== idx));
    } catch (err) {
      console.error('Delete failed:', err);
    }
  };

  const send = async () => {
    if (!input.trim() && attachments.length === 0) return;
    setSending(true);

    const tempId = `temp-${Date.now()}`;
    const tempMessage = {
      tempId,
      formId: mergedTicket.id,
      senderName: caName,
      senderId: caId,
      senderRole: caRole,
      message: input,
      attachments,
      timestamp: new Date().toISOString(),
      isCurrentUser: true
    };

    setMessages(prev => [...prev, normalizeMessage(tempMessage)]);
    setInput('');
    setAttachments([]);

    try {
      if (socketRef.current?.connected) {
        socketRef.current.emit('sendMessage', {
          formId: mergedTicket.id,
          senderId: caId,
          senderRole: 'CA',
          senderName: caName,
          message: input,
          attachments,
          tempId
        });
      }
    } catch (err) {
      console.error('Failed to send:', err);
      setMessages(prev => prev.filter(m => m.tempId !== tempId));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex h-screen bg-gray-50">
      <CaSidebar isOpen />
      <div className="flex-1 ml-20 flex flex-col">
        <CaHeader fullName={caName} onClose={onClose} />

        <div className="flex items-center justify-between bg-white px-6 py-4 border-b">
          <div className="flex items-center space-x-4">
            <Link to="/ticket-interface"><ArrowLeft /></Link>
            <h1 className="text-md font-semibold">{mergedTicket.title}</h1>
          </div>
          <StatusBadge status={status} onChange={handleStatusChange} />
        </div>

        <div className="bg-white px-6 border-b">
          <button className="py-3 text-blue-600 border-b-2 border-blue-500">Conversation</button>
        </div>

        <div className="flex-1 flex flex-col overflow-hidden">
          <div
            ref={scrollRef}
            className="flex-1 overflow-auto bg-gradient-to-b from-gray-50 to-white p-6 space-y-6"
          >

            {(responseData?.userEmail || responseData?.userPhone) && (
              <div className="flex justify-start">
                <div className="flex items-start space-x-3 max-w-2xl">
                  <Avatar name={startupInfo.fullName} />
                  <div className="flex flex-col items-start">
                    <div className="flex items-center space-x-2 mb-2">
                      <span className="text-sm font-medium text-gray-900">
                        {startupInfo.fullName}
                      </span>
                      <span className="text-xs text-gray-500">
                        {formatTime(responseData?._createdAt || new Date().toISOString())}
                      </span>
                    </div>
                    <div className="bg-blue-50 border border-blue-200 rounded-2xl px-4 py-3 shadow-sm">
                      <span className="font-medium text-blue-800">Contact Information:</span>
                      <div className="mt-2 space-y-1 text-sm text-blue-700">
                        {responseData?.userEmail && (
                          <div className="flex items-center">
                            <span className="mr-2">📧</span>
                            <span>{responseData.userEmail}</span>
                          </div>
                        )}
                        {responseData?.userPhone && (
                          <div className="flex items-center">
                            <span className="mr-2">📞</span>
                            <span>{responseData.userPhone}</span>
                          </div>
                        )}
                      </div>
                      <div className="mt-2 text-xs text-blue-600">
                        This information was provided with the form submission
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Display form fields and responses */}
            {fields
              .filter(f => {
                const ans = responseData[f._id];
                return typeof ans === 'string' && ans.trim().length > 0;
              })
              .map(f => {
                const answer = responseData[f._id];
                const isUrl = /^https?:\/\//.test(answer);
                const fileName = isUrl ? answer.split('/').pop() : answer;

                return (
                  <div key={f._id} className="flex justify-start">
                    <div className="flex items-start space-x-3 max-w-2xl">
                      <Avatar name={startupInfo.fullName} />
                      <div className="flex flex-col items-start">
                        <div className="flex items-center space-x-2 mb-2">
                          <span className="text-sm font-medium text-gray-900">
                            {startupInfo.fullName}
                          </span>
                          <span className="text-xs text-gray-500">
                            {formatTime(responseData.createdAt || new Date().toISOString())}
                          </span>
                        </div>
                        <div className="bg-white border rounded-2xl px-4 py-3 shadow-sm text-gray-900">
                          <span className="font-medium">{f.name}:</span>
                          {isUrl ? (
                            <div className="mt-2 flex items-center space-x-2">
                              <button
                                onClick={() => handleFilePreview({
                                  name: fileName,
                                  url: answer,
                                  type: answer.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? 'image' :
                                    answer.endsWith('.pdf') ? 'pdf' : 'doc'
                                })}
                                className="flex items-center space-x-2 p-2 rounded-lg bg-gray-100 hover:bg-gray-200"
                              >
                                {getFileIcon(null, answer)}
                                <span className="truncate max-w-xs">{fileName}</span>
                              </button>
                              <button
                                onClick={() => handleDownloadFile(answer, fileName)}
                                className="p-2 rounded-full text-gray-400 hover:text-gray-600"
                                title="Download"
                              >
                                <Download className="w-4 h-4" />
                              </button>
                            </div>
                          ) : (
                            <span className="ml-1">{answer}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}

            {messages.map(msg => (
              <div key={msg.id} className={`flex message-container ${msg.isCurrentUser ? 'justify-end' : 'justify-start'}`}>
                <div className={`flex items-start space-x-3 max-w-2xl ${msg.isCurrentUser ? 'flex-row-reverse space-x-reverse' : ''}`}>
                  <Avatar name={msg.senderName} />
                  <div className={`flex flex-col ${msg.isCurrentUser ? 'items-end' : 'items-start'}`}>
                    <div className="flex items-center space-x-2 mb-2">
                      <span className="text-sm font-medium text-gray-900">
                        {msg.isCurrentUser ? 'You' : msg.senderName}
                      </span>
                      {msg.timestamp && (
                        <span className="text-xs text-gray-500">{formatTime(msg.timestamp)}</span>
                      )}
                    </div>
                    <div className={`rounded-2xl px-4 py-3 shadow-sm ${msg.isCurrentUser ? 'bg-blue-500 text-white' : 'bg-white border border-gray-200 text-gray-900'}`}>
                      <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
                        {msg.message || ''}
                      </ReactMarkdown>
                      {msg.attachments?.map((att, i) => (
                        <div key={i} className="mt-2 flex items-center space-x-2">
                          <button
                            onClick={() => handleFilePreview(att)}
                            className={`flex items-center space-x-2 p-2 rounded-lg ${msg.isCurrentUser ? 'bg-blue-400 hover:bg-blue-600' : 'bg-gray-100 hover:bg-gray-200'}`}
                          >
                            {getFileIcon(att.type, att.url)}
                            <span className="truncate max-w-xs">{att.name}</span>
                          </button>
                          <button
                            onClick={() => handleDownloadFile(att.url, att.name)}
                            className={`p-2 rounded-full ${msg.isCurrentUser ? 'text-blue-100 hover:text-white' : 'text-gray-400 hover:text-gray-600'}`}
                            title="Download"
                          >
                            <Download className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                    <div className="flex justify-end mt-1">
                      <MessageStatus status={msg.status} isCurrentUser={msg.isCurrentUser} />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {previewFile && (
            <FilePreviewModal
              file={previewFile}
              onClose={() => setPreviewFile(null)}
            />
          )}

          <div className="bg-white border-t p-4 shadow-lg">
            {attachments.length > 0 && (
              <div className="mb-4 bg-gray-50 rounded-lg border p-3">
                <div className="flex justify-between mb-2">
                  <span className="font-medium text-gray-700">
                    Attachments ({attachments.length})
                  </span>
                </div>
                <div className="space-y-2">
                  {attachments.map((att, idx) => (
                    <div key={idx} className="flex items-center space-x-3 bg-white rounded border p-2">
                      {getFileIcon(att.type, att.url)}
                      <div className="flex-1 truncate">
                        <p className="truncate font-medium text-gray-900">{att.name}</p>
                      </div>
                      <button
                        onClick={() => removeAttachment(idx)}
                        className="text-gray-400 hover:text-red-500"
                      >
                        <XCircle className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="mb-3 bg-gradient-to-r from-gray-50 to-blue-50 rounded-lg border p-3 relative">
              <div className="flex space-x-2">
                <button onClick={() => wrapSelection("**")} title="Bold" className="p-2 hover:bg-blue-100 rounded">
                  <strong>B</strong>
                </button>
                <button onClick={() => wrapSelection("*")} title="Italic" className="p-2 hover:bg-blue-100 rounded">
                  <Italic className="w-4" />
                </button>
                <button onClick={insertLink} title="Link" className="p-2 hover:bg-blue-100 rounded">🔗</button>
                <button onClick={() => setShowEmojiPicker(v => !v)} title="Emoji" className="p-2 hover:bg-yellow-100 rounded">
                  😊
                </button>
              </div>
              {showEmojiPicker && (
                <div className="absolute bottom-full left-0 mb-2 z-50">
                  <EmojiPicker onEmojiClick={handleEmojiClick} />
                </div>
              )}
            </div>

            <div className="flex items-end space-x-3">
              <div className="relative flex-1">
                <textarea
                  ref={textareaRef}
                  rows={3}
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={handleKeyPress}
                  placeholder="Type message..."
                  className="w-full resize-none border rounded-xl px-4 py-3 pr-16 focus:ring-2 focus:ring-blue-500 shadow-sm"
                />
                <div className="absolute bottom-3 right-3 flex space-x-2">
                  <div className="relative">
                    <button onClick={() => setShowAttachMenu(v => !v)} className="p-1.5 hover:bg-blue-100 rounded text-gray-400 hover:text-blue-600">
                      <Paperclip className="w-4 h-4" />
                    </button>
                    {showAttachMenu && (
                      <div className="absolute bottom-full right-0 mb-2 bg-white border rounded shadow py-2 min-w-[180px] z-50">
                        <button
                          onClick={() => fileInputRef.current.click()}
                          className="w-full px-4 py-2 hover:bg-gray-50 text-left flex items-center space-x-2 text-sm"
                        >
                          <FileText className="w-4 h-4" /><span>Upload Document</span>
                        </button>
                        <button
                          onClick={() => imageInputRef.current.click()}
                          className="w-full px-4 py-2 hover:bg-gray-50 text-left flex items-center space-x-2 text-sm"
                        >
                          <ImageIcon className="w-4 h-4" /><span>Upload Image</span>
                        </button>
                      </div>
                    )}
                  </div>
                  <button
                    onClick={send}
                    disabled={sending || (!input.trim() && attachments.length === 0)}
                    className="p-3 rounded-xl bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 disabled:from-gray-300 disabled:to-gray-300 text-white shadow-lg hover:shadow-xl transform hover:scale-105 transition-all"
                  >
                    <SendIcon className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>

            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".pdf,.doc,.docx,.txt"
              onChange={handleFileUpload}
              className="hidden"
            />
            <input
              ref={imageInputRef}
              type="file"
              multiple
              accept="image/*"
              onChange={handleFileUpload}
              className="hidden"
            />
          </div>
        </div>
      </div>
    </div>
  );
}